import React from 'react';
import { Box, Slider, Typography } from '@mui/material';

/**
 * Reusable 1-10 Scale Rating Input Component
 *
 * Visual slider with labeled endpoints and mark labels for each value.
 * Accessible, touch-friendly, and responsive for mobile use.
 *
 * @param {Object} props
 * @param {number} props.value - Current selected value (1-10)
 * @param {function} props.onChange - Callback when value changes: (newValue) => void
 * @param {number} [props.min=1] - Minimum scale value
 * @param {number} [props.max=10] - Maximum scale value
 * @param {string} [props.minLabel='Strongly Disagree'] - Label for the minimum end
 * @param {string} [props.maxLabel='Strongly Agree'] - Label for the maximum end
 * @param {boolean} [props.disabled=false] - Whether the input is disabled
 * @param {object} [props.sx] - Additional MUI sx styles for the container
 */
function RatingScale({
  value,
  onChange,
  min = 1,
  max = 10,
  minLabel = 'Strongly Disagree',
  maxLabel = 'Strongly Agree',
  disabled = false,
  sx,
}) {
  const marks = Array.from({ length: max - min + 1 }, (_, i) => ({
    value: min + i,
    label: String(min + i),
  }));

  return (
    <Box sx={sx}>
      <Box sx={{ px: 2 }}>
        <Slider
          value={value ?? min}
          onChange={(e, newVal) => onChange(newVal)}
          // Tim 26 Oct (D05): leaving the thumb at "1" and clicking it never fired onChange (same value),
          // so "1" was not recorded as an answer. onChangeCommitted fires on every click/release.
          onChangeCommitted={(e, newVal) => onChange(newVal)}
          min={min}
          max={max}
          step={1}
          marks={marks}
          valueLabelDisplay="auto"
          disabled={disabled}
          aria-label={`Rating scale from ${min} to ${max}`}
          sx={{
            '& .MuiSlider-markLabel': {
              fontSize: '0.75rem',
            },
            '& .MuiSlider-thumb': {
              width: 20,
              height: 20,
              // Unanswered: a hollow thumb, so "1" is visibly a choice and not a default
              ...(value === undefined && { bgcolor: 'background.paper', border: 2, borderColor: 'primary.main' }),
              // Touch-friendly: larger hit area on mobile
              '@media (pointer: coarse)': {
                width: 28,
                height: 28,
              },
            },
            '& .MuiSlider-track': {
              height: 6,
              ...(value === undefined && { opacity: 0 }),
            },
            '& .MuiSlider-rail': {
              height: 6,
            },
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {minLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {maxLabel}
        </Typography>
      </Box>
    </Box>
  );
}

export default RatingScale;
